import clsx from 'clsx';
import { LinkIcon } from '@heroicons/react/24/outline';
import type { ArquivoDocumento } from '../../publicacoes/publicacao.types';
import { publicacaoService } from '../../publicacoes/publicacaoService';
import { ChipArquivo, EnvioArquivo } from '../../publicacoes/components/EnvioArquivo';

/** Escolha do arquivo do documento: enviar (PDF/imagem, sobe na hora) ou link externo. */
export interface EscolhaArquivo {
  modo: 'arquivo' | 'link';
  /** Enviado nesta edição (já está no Storage). */
  enviado: ArquivoDocumento | null;
  /** A pessoa clicou no X do arquivo que já estava salvo. */
  removerAtual: boolean;
  linkUrl: string;
  linkNome: string;
}

export const ESCOLHA_VAZIA: EscolhaArquivo = { modo: 'arquivo', enviado: null, removerAtual: false, linkUrl: '', linkNome: '' };

export function escolhaDe(arquivo: ArquivoDocumento | null | undefined): EscolhaArquivo {
  if (arquivo?.tipo === 'link') return { ...ESCOLHA_VAZIA, modo: 'link', linkUrl: arquivo.url, linkNome: arquivo.nome };
  return ESCOLHA_VAZIA;
}

export function linkValido(url: string): boolean {
  return /^https?:\/\/\S+\.\S+/i.test(url.trim());
}

/** Tem arquivo/link válido escolhido? */
export function escolhaCompleta(escolha: EscolhaArquivo, atual: ArquivoDocumento | null | undefined): boolean {
  if (escolha.modo === 'link') return linkValido(escolha.linkUrl);
  return Boolean(escolha.enviado) || (atual?.tipo === 'arquivo' && !escolha.removerAtual);
}

/** Arquivo final: o enviado agora, o link, ou o que já estava salvo. */
export function arquivoDaEscolha(escolha: EscolhaArquivo, atual: ArquivoDocumento | null | undefined): ArquivoDocumento {
  if (escolha.modo === 'link') {
    const url = escolha.linkUrl.trim();
    return { tipo: 'link', url, nome: escolha.linkNome.trim() || url, path: null, contentType: null };
  }
  if (escolha.enviado) return escolha.enviado;
  if (atual && atual.tipo === 'arquivo' && !escolha.removerAtual) return atual;
  throw new Error('Escolha o arquivo.');
}

/** Fechar sem salvar: o que subiu nesta edição não fica órfão. */
export function descartarEnvio(escolha: EscolhaArquivo): void {
  if (escolha.enviado?.path) void publicacaoService.removerArquivo(escolha.enviado.path);
}

interface ArquivoDocumentoCampoProps {
  publicacaoId: string;
  valor: EscolhaArquivo;
  onChange: (valor: EscolhaArquivo) => void;
  /** Arquivo já salvo (editar sem trocar). */
  atual?: ArquivoDocumento | null;
  rotulo?: string;
}

const OPCAO = 'flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors';

export function ArquivoDocumentoCampo({ publicacaoId, valor, onChange, atual, rotulo = 'Arquivo *' }: ArquivoDocumentoCampoProps) {
  const alterar = (parcial: Partial<EscolhaArquivo>) => onChange({ ...valor, ...parcial });
  const atualVisivel = atual?.tipo === 'arquivo' && !valor.removerAtual && !valor.enviado ? atual : null;

  return (
    <div className="space-y-2">
      <span className="block text-sm font-medium text-raro-gray-700">{rotulo}</span>
      <div className="flex flex-wrap gap-2">
        {(
          [
            { modo: 'arquivo', label: 'Enviar arquivo (PDF ou imagem)' },
            { modo: 'link', label: 'Link externo' },
          ] as const
        ).map((opcao) => (
          <label
            key={opcao.modo}
            className={clsx(
              OPCAO,
              valor.modo === opcao.modo ? 'border-primary-300 bg-primary-50 font-semibold text-primary-800' : 'border-raro-gray-200 text-raro-gray-700'
            )}
          >
            <input type="radio" checked={valor.modo === opcao.modo} onChange={() => alterar({ modo: opcao.modo })} className="text-primary-600" />
            {opcao.label}
          </label>
        ))}
      </div>

      {valor.modo === 'arquivo' ? (
        valor.enviado ? (
          <ChipArquivo
            anexo={{ nome: valor.enviado.nome, url: valor.enviado.url, path: valor.enviado.path ?? '', contentType: valor.enviado.contentType ?? '' }}
            recemEnviado
            onRemover={() => {
              descartarEnvio(valor);
              alterar({ enviado: null });
            }}
          />
        ) : atualVisivel ? (
          <ChipArquivo
            anexo={{ nome: atualVisivel.nome, url: atualVisivel.url, path: atualVisivel.path ?? '', contentType: atualVisivel.contentType ?? '' }}
            onRemover={() => alterar({ removerAtual: true })}
          />
        ) : (
          <EnvioArquivo
            publicacaoId={publicacaoId}
            pasta="documento"
            accept="application/pdf,image/png,image/jpeg,image/webp,text/html"
            texto="Escolher PDF, imagem ou apresentação HTML (até 15 MB)"
            onEnviado={(anexo) =>
              alterar({ enviado: { tipo: 'arquivo', nome: anexo.nome, url: anexo.url, path: anexo.path, contentType: anexo.contentType } })
            }
          />
        )
      ) : (
        <div className="grid gap-2 sm:grid-cols-[1fr_12rem]">
          <label className="relative block">
            <span className="sr-only">Endereço do link</span>
            <LinkIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-raro-gray-400" aria-hidden="true" />
            <input
              className="input-primary w-full pl-9"
              placeholder="https://..."
              value={valor.linkUrl}
              onChange={(event) => alterar({ linkUrl: event.target.value })}
            />
          </label>
          <input
            className="input-primary w-full"
            placeholder="Nome do link (opcional)"
            value={valor.linkNome}
            onChange={(event) => alterar({ linkNome: event.target.value })}
          />
        </div>
      )}
    </div>
  );
}
